import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { Device, from, until } from '@helpers/media';
import srOnly from '@helpers/srOnly';
import { link, paragraphRegular } from '@helpers/typography';
import { m } from 'framer-motion';
import { rgba } from 'polished';
import styled from 'styled-components';
import { ContentType } from '../../SectionWrapper/SectionWrapper';
import Textbox from '../Inputs/Textbox/Textbox';
import TextboxStyles from '../Inputs/Textbox/Textbox.styles';
const Container = styled.div `
  ${transition('opacity')};

  --textLinkColour: ${brand.global.black};

  position: relative;

  &:has(input[aria-disabled='true'], button[aria-disabled='true']) {
    opacity: 0.5;
    pointer-events: none;
  }
`;
const Form = styled.form `
  ${transition('border-color')};

  display: flex;
  align-items: center;
  gap: 8px;
  border-radius: 40px;
  border: 1px solid ${brand.grey.light};
  width: 100%;
  padding: 0 6px;

  &:has(span) {
    border-color: ${brand.validation.invalid} !important;
  }

  ${TextboxStyles.InputWrapper} {
    display: contents;
  }
`;
const Label = styled.label `
  ${srOnly};
`;
const Input = styled(Textbox) `
  ${fonts.proximaNova.semiBold};

  --borderRadius: 40px;
  --textColour: ${brand.blue.navy};

  border: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  margin: 0;
  padding-top: 0;
  padding-bottom: 0;
  height: ${fluid(44, 48)};
  font-size: 16px;
  line-height: 26px;

  &::placeholder {
    color: ${brand.grey.dark};
  }
`;
const Button = styled(m.button) `
  ${ButtonReset};
  ${transition('background-color, color')};

  --primaryColour: ${brand.green.default};
  --contrastColour: ${brand.global.white};

  display: grid;
  place-content: center;
  width: ${fluid(32, 40)};
  height: ${fluid(32, 40)};
  border-radius: 50%;
  background-color: var(--primaryColour);
  color: var(--contrastColour);
  border: 2px solid var(--primaryColour);

  svg {
    display: block;
    width: ${fluid(18, 24)};
  }

  &:focus-visible,
  &:hover {
    color: var(--primaryColour);
    background-color: var(--contrastColour);
  }

  &[data-type=${ContentType.Savings}] {
    --primaryColour: ${brand.blue.default};
  }

  &[data-type=${ContentType.Intermediaries}] {
    --primaryColour: ${brand.blue.navy};
    --contrastColour: ${brand.green.light};
  }

  &[data-type='clear'] {
    --primaryColour: ${brand.green.light};
    --contrastColour: ${brand.blue.navy};

    svg {
      width: ${fluid(12, 16)};
    }
  }

  /* Mobile click styles - button should mimic press */
  @media ${until(Device.TabletLarge)} {
    ${transition('transform', 0.15)};

    &:active {
      transform: translateY(1.5px);
    }
  }
`;
const Suggestions = styled.ul `
  ${transition('opacity, transform')};

  --borderRadius: ${fluid(10, 20)};

  position: absolute;
  z-index: 1;
  top: calc(100% + 16px);
  left: 0;
  background-color: ${brand.global.white};
  box-shadow: 0px 1px 4px 0px ${rgba(brand.blue.navy, 0.1)};
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
  border-radius: var(--borderRadius) var(--borderRadius) var(--borderRadius) 0px;
  width: 100%;

  ${Container}:has(${Input}[aria-expanded='false']) & {
    opacity: 0;
    pointer-events: none;
    transform: translateY(-20px);
  }

  @media ${from(Device.TabletLarge)} {
    min-width: 424px;
  }
`;
const AutosuggestOption = styled.button `
  ${ButtonReset};
  ${paragraphRegular};
  ${fonts.proximaNova.bold};
  ${link};

  padding: ${fluid(8, 16)};
  text-align: left;
  margin: 0;
  color: ${brand.global.black};
  text-decoration-color: transparent;

  > span {
    color: ${brand.green.default};
  }

  &:focus-visible,
  &:hover {
    text-decoration-color: transparent;
  }
`;
const SearchboxStyles = {
    Container,
    Button,
    Label,
    Input,
    AutosuggestOption,
    Form,
    Suggestions,
};
export default SearchboxStyles;
